'use client'

import Header from '@/components/Header'
import Footer from '@/components/Footer'
import EventCard from '@/components/EventCard'
import EventCardSkeleton from '@/components/EventCardSkeleton'
import AnimatedSection from '@/components/AnimatedSection'
import HeroSection from '@/components/HeroSection'
import { FaInstagram, FaWhatsapp } from 'react-icons/fa'
import { useEffect, useState } from 'react'

export default function HomePage() {
  const [events, setEvents] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch('/api/events/next')
      .then(res => res.json())
      .then(data => {
        setEvents(Array.isArray(data) ? data : [])
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  return (
    <>
      <Header />
      <main className="flex-1 bg-black min-h-screen">
        {/* Hero Section */}
        <HeroSection />

        {/* Events Section */}
        <section id="eventos" className="py-16 sm:py-24 md:py-32 relative">
          {/* Background Pattern sutil para continuidade */}
          <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.02]"></div>
          <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-black to-transparent z-10"></div>

          <div className="container mx-auto px-4 relative z-10">
            <AnimatedSection className="text-center mb-20">
              <h2 className="text-3xl sm:text-4xl md:text-6xl font-black mb-6 tracking-tight">
                <span className="text-white">PRÓXIMOS </span>
                <span className="bg-gradient-to-r from-[#ffa300] to-yellow-500 bg-clip-text text-transparent">
                  EVENTOS
                </span>
              </h2>
              <p className="text-xl text-zinc-400 max-w-2xl mx-auto font-light">
                Não fique de fora. Garanta seu lugar nas melhores festas da temporada.
              </p>
            </AnimatedSection>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-[500px]">
                    <EventCardSkeleton />
                  </div>
                ))}
              </div>
            ) : events.length === 0 ? (
              <div className="text-center py-24 bg-zinc-900/30 rounded-3xl border border-zinc-800/50">
                <p className="text-zinc-500 text-xl font-light">Nenhum evento disponível no momento.</p>
                <p className="text-[#ffa300] mt-2 font-medium">Fique ligado nas novidades!</p>
              </div>
            ) : (
              <div
                className={`grid gap-8 ${
                  events.length === 1
                    ? 'grid-cols-1 place-items-center max-w-2xl mx-auto'
                    : events.length === 2
                      ? 'grid-cols-1 sm:grid-cols-2 max-w-5xl mx-auto'
                      : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                }`}
              >
                {events.map(event => (
                  <EventCard
                    key={event.id}
                    id={event.id}
                    title={event.title}
                    date={event.date}
                    time={event.time}
                    location={event.location}
                    description={event.description}
                    imageUrl={event.imageUrl}
                  />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 sm:py-24 md:py-32 bg-zinc-900 relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03]"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#ffa300]/10 to-transparent opacity-30"></div>
          
          <div className="container mx-auto px-4 text-center relative z-10">
            <h3 className="text-3xl sm:text-4xl md:text-7xl font-black mb-8 leading-none tracking-tighter uppercase text-white">
              Sua próxima<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ffa300] to-yellow-400">
                grande história
              </span>
            </h3>
            <p className="text-xl md:text-2xl text-zinc-400 mb-12 max-w-2xl mx-auto font-light">
              Milhares de universitários já vivem a experiência Vibe. Só falta você.
            </p>
          </div>
        </section>

        {/* Seção Contato / Pré-Footer */}
        <section id="contato" className="py-24 bg-black relative border-t border-zinc-900">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-16 items-center">
              <div className="text-left">
                <h3 className="text-3xl sm:text-4xl md:text-5xl font-black mb-6 text-white tracking-tight">
                  FALE COM A <br />
                  <span className="text-[#ffa300]">EQUIPE VIBE</span>
                </h3>
                <p className="text-lg text-zinc-400 mb-8 font-light leading-relaxed">
                  Tem alguma dúvida sobre ingressos, eventos ou parcerias? 
                  Nossa equipe está pronta para te atender em qualquer um dos nossos canais oficiais.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                  <a 
                    href="https://www.instagram.com/vibe_solucoes/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="group flex items-center justify-center gap-3 px-6 py-3 min-h-[48px] bg-zinc-900 border border-zinc-800 rounded-full hover:border-[#ffa300] transition-all hover:-translate-y-1"
                  >
                    <FaInstagram className="text-2xl text-white group-hover:text-[#ffa300] transition-colors" />
                    <span className="text-white font-medium group-hover:text-[#ffa300] transition-colors">Instagram</span>
                  </a>
                  
                  <a 
                    href="https://api.whatsapp.com/send/?phone=5599982170800&text&type=phone_number&app_absent=0" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="group flex items-center justify-center gap-3 px-6 py-3 min-h-[48px] bg-zinc-900 border border-zinc-800 rounded-full hover:border-[#ffa300] transition-all hover:-translate-y-1"
                  >
                    <FaWhatsapp className="text-2xl text-white group-hover:text-[#ffa300] transition-colors" />
                    <span className="text-white font-medium group-hover:text-[#ffa300] transition-colors">WhatsApp</span>
                  </a>
                </div>
              </div>

              {/* Visual Element para Contato (pode ser um mapa estilizado ou gráfico abstrato) */}
              <div className="relative h-[280px] sm:h-[350px] md:h-[400px] rounded-3xl overflow-hidden bg-zinc-900 border border-zinc-800 flex items-center justify-center group">
                 <div className="absolute inset-0 bg-gradient-to-br from-[#ffa300]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                 <div className="text-center">
                    <p className="text-zinc-500 font-mono text-sm mb-2">ATENDIMENTO</p>
                    <p className="text-white text-2xl font-bold">Seg - Sex</p>
                    <p className="text-[#ffa300] text-xl">09:00 - 18:00</p>
                 </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  )
}
